import { toMap } from "@omni/utils/array.utils";
import { attempt } from "@omni/utils/function.utils";
import { isDefined } from "@omni/utils/helper.utils";
import { cn } from "@omni/utils/styles";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";

import { FaqContextProvider } from "@/components/General/Faq/context/faq.context";

import { pushSelection, readSelection } from "./helpers/url.helpers";
import { FaqCategory } from "./types/faqCategory.types";
import { FaqQuestion } from "./types/faqQuestion.types";
import { FaqSelection } from "./types/faqSelection.types";

type Props = {
    json: string;
    className?: string;
    children: ReactNode;
};

export const Base = ({ json, className, children }: Props) => {
    const categories = useMemo(
        () =>
            attempt<Array<FaqCategory>>(() => JSON.parse(json).categories, []),
        [json]
    );

    const initialSelection = useMemo(
        () => ({
            category: categories[0],
            question: categories[0]?.faqQuestions[0]
        }),
        [categories]
    );

    const categoryByAliasMap = useMemo(
        () => categories.reduce(toMap("alias"), new Map<string, FaqCategory>()),
        [categories]
    );

    const questionByAliasMap = useMemo(
        () =>
            categories
                .flatMap((category) => category.faqQuestions)
                .reduce(toMap("alias"), new Map<string, FaqQuestion>()),
        [categories]
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    const pushSelectionToUrl = useCallback(
        pushSelection(categoryByAliasMap, questionByAliasMap),
        [categoryByAliasMap, questionByAliasMap, pushSelection]
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    const readSelectionFromUrl = useCallback(
        () =>
            readSelection(categoryByAliasMap, questionByAliasMap)() ??
            initialSelection,
        [
            categoryByAliasMap,
            questionByAliasMap,
            initialSelection,
            readSelection
        ]
    );

    const [selection, setSelection] = useState<FaqSelection>(
        readSelectionFromUrl()
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        const { signal, abort } = new AbortController();

        window.addEventListener(
            "popstate",
            () => {
                const selection = readSelectionFromUrl();
                if (!isDefined(selection)) return;

                setSelection(selection);
            },
            { signal }
        );

        return abort;
    }, [categories, setSelection, readSelectionFromUrl]);

    const handleOnSelectCategory = (category: FaqCategory) => {
        const question = category.faqQuestions.at(0);

        if (!isDefined(question)) return;

        const newSelection = {
            category: category,
            question: question
        };

        pushSelectionToUrl(newSelection);
        setSelection(newSelection);
    };

    const handleOnToggleQuestion = (question: FaqQuestion) =>
        setSelection((currentSelection) => {
            const newSelection = {
                category: currentSelection.category,
                question:
                    currentSelection.question === question
                        ? undefined
                        : question
            };

            pushSelectionToUrl(newSelection);

            return newSelection;
        });

    return (
        <FaqContextProvider
            categories={categories}
            selection={selection}
            onSelectCategory={handleOnSelectCategory}
            onToggleQuestion={handleOnToggleQuestion}
        >
            <div
                className={cn(
                    "tw:flex tw:gap-4",
                    "tw:flex-col",
                    "tw:md:flex-row",
                    className
                )}
            >
                {children}
            </div>
        </FaqContextProvider>
    );
};
